<template>
  <AdminNavbar>
    <h1 class="text-lg font-bold">Уулзалт товлох эрхтэй хэрэглэгчид</h1>

    <div v-if="!isEdit" class="bg-white rounded shadow">
      <h6 class="text-base border-t-4 border-t-blue-500 rounded-t border-b p-3">
        Хэрэглэгчид
      </h6>

      <div class="p-3 space-y-5">
        <!-- PAGINATION -->
        <div class="flex justify-between items-center text-xs">
          <div class="space-x-3">
            <span>Хэрэглэгчийн төрөл:</span>
            <select
              name="selectedType"
              id="selectedType"
              class="border rounded p-1 text-blue-500 max-w-[20rem]"
              v-model="selectedType"
            >
              <option value="all">Бүгд</option>
              <option value="Exhibitor">Exhibitor</option>
              <option value="Attendee">Attendee</option>
              <option value="Sponsor">Sponsor</option>
            </select>
          </div>

          <div class="space-x-3">
            <span>Хуудас бүрт:</span>
            <select
              name="pagePerPage"
              id="pagePerPage"
              class="border rounded p-1 text-blue-500"
              v-model="dataPerPage"
            >
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>

          <span>Нийт хэрэглэгчдийн тоо: {{ totalUsersCount }} </span>

          <div class="flex items-center gap-3">
            <span>Нийт хуудас: {{ totalPages }} </span>
            <PaginationCart
              :totalPages="totalPages"
              :modelValue="currentPage"
              :style="{
                color: 'blue-500',
                height: 7,
                width: 7,
                fontSize: 'xs',
              }"
              @page-changed="onPageChanged"
            />
          </div>
        </div>

        <div>
          <div v-if="users.length > 0 && !isLoading" class="p-3">
            <hot-table :settings="hotSettings" :data="users"></hot-table>
          </div>
          <div
            v-else-if="users.length === 0 && !isLoading"
            class="flex justify-center items-center h-[60vh]"
          >
            Жагсаалт хоосон байна
          </div>

          <div v-else class="flex justify-center items-center h-[60vh]">
            <SpinLoading :color="'rgb(59 130 246)'" />
          </div>
        </div>
      </div>
    </div>

    <YellowEditMeetingUser
      v-else
      :data="selectedUser"
      @closeEdit="switchEdit"
    />
  </AdminNavbar>
</template>

<script>
import { onMounted, ref, watch } from "vue";
import AdminNavbar from "@/components/admin/AdminNavbar.vue";
import adminInstance from "@/lib/adminInstance";
import SpinLoading from "@/components/ui/SpinLoading.vue";
import PaginationCart from "@/components/ui/PaginationCart.vue";
import { HotTable } from "@handsontable/vue3";
import { registerAllModules } from "handsontable/registry";
import "handsontable/dist/handsontable.full.min.css";
import "handsontable/dist/handsontable.full.css";
import YellowEditMeetingUser from "../../../containers/YellowPage/admin/YellowEditMeetingUser";

registerAllModules();

export default {
  name: "AdminYellowMeetingUsers",
  components: {
    AdminNavbar,
    SpinLoading,
    PaginationCart,
    HotTable,
    YellowEditMeetingUser,
  },
  setup() {
    const isLoading = ref(false);
    const users = ref([]);
    const dataPerPage = ref(20);
    const totalUsersCount = ref(0);
    const totalPages = ref(1);
    const currentPage = ref(1);
    const selectedType = ref("all");
    const isEdit = ref(false);

    const fetchUsers = async () => {
      try {
        isLoading.value = true;
        const res = await adminInstance.get(
          `/yellow-page/get/meeting-users?type=${selectedType.value}&page=${currentPage.value}&limit=${dataPerPage.value}`
        );
        if (res.status === 200) {
          console.log(res.data);
          users.value = res.data.users;
          totalUsersCount.value = res.data.total;
          totalPages.value = res.data.totalPages;
        }
      } catch (err) {
        return err;
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(async () => {
      await fetchUsers();
    });

    const onPageChanged = (page) => {
      currentPage.value = page;
      fetchUsers();
    };

    watch([() => dataPerPage.value, () => selectedType.value], () => {
      currentPage.value = 1;
      fetchUsers();
    });

    const commonRenderer = (instance, td, row, col, prop, value) => {
      td.classList.add("htMiddle");
      td.innerHTML = `<span class="flex justify-center p-2 text-black">${
        value ? value : ""
      }</span>`;
      return td;
    };

    const hotSettings = {
      colHeaders: [
        "Үйлдэл",
        "Нэр /монголоор/",
        "Нэр /англиар/",
        "Цахим шуудан",
        "Утасны дугаар",
        "Төрөл",
      ],
      columns: [
        {
          readOnly: true,
          renderer: function (inst, td, row) {
            const user = users.value[row];
            td.classList.add("htCenter", "htMiddle", "htButton");
            td.innerHTML = `<div class="flex justify-center items-center p-2 gap-2 text-white">
                    <button class="flex items-center bg-sky-500 py-1 px-2 rounded edit-btn">
                        <svg class="fill-current w-3 h-3 mr-1" viewBox="0 0 512 512"><path d="M362.7 19.3L314.3 67.7 444.3 197.7l48.4-48.4c25-25 25-65.5 0-90.5L453.3 19.3c-25-25-65.5-25-90.5 0zm-71 71L58.6 323.5c-10.4 10.4-18 23.3-22.2 37.4L1 481.2C-1.5 489.7 .8 498.8 7 505s15.3 8.5 23.7 6.1l120.3-35.4c14.1-4.2 27-11.8 37.4-22.2L421.7 220.3 291.7 90.3z"/></svg>
                        <span>Засварлах</span>
                    </button>
                    <button class="flex justify-center items-center bg-red-500 py-1 px-2 rounded delete-btn">
                        <svg class="fill-current w-3 h-3 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><!--!Font Awesome Free 6.5.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.--><path d="M135.2 17.7C140.6 6.8 151.7 0 163.8 0H284.2c12.1 0 23.2 6.8 28.6 17.7L320 32h96c17.7 0 32 14.3 32 32s-14.3 32-32 32H32C14.3 96 0 81.7 0 64S14.3 32 32 32h96l7.2-14.3zM32 128H416V448c0 35.3-28.7 64-64 64H96c-35.3 0-64-28.7-64-64V128zm96 64c-8.8 0-16 7.2-16 16V432c0 8.8 7.2 16 16 16s16-7.2 16-16V208c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16V432c0 8.8 7.2 16 16 16s16-7.2 16-16V208c0-8.8-7.2-16-16-16zm96 0c-8.8 0-16 7.2-16 16V432c0 8.8 7.2 16 16 16s16-7.2 16-16V208c0-8.8-7.2-16-16-16z"/></svg>
                        <span>Устгах</span>
                    </button>
                </div>`;

            const editButton = td.querySelector(".edit-btn");
            if (editButton) {
              editButton.addEventListener("click", () => {
                editUser(user);
              });
            }

            const deleteButton = td.querySelector(".delete-btn");
            if (deleteButton) {
              deleteButton.addEventListener("click", () => {
                deleteUser(user.id);
              });
            }
          },
        },
        {
          data: "name",
          readOnly: true,
          renderer: commonRenderer,
        },
        {
          data: "name_en",
          readOnly: true,
          renderer: commonRenderer,
        },
        {
          data: "email",
          readOnly: true,
          renderer: commonRenderer,
        },
        {
          data: "phone_number",
          readOnly: true,
          renderer: commonRenderer,
        },
        {
          data: "user_type",
          readOnly: true,
          renderer: commonRenderer,
        },
      ],
      rowHeaders: true,
      width: "100%",
      height: "60vh",
      contextMenu: true,
      manualColumnFreeze: true,
      manualColumnMove: true,
      manualColumnResize: true,
      manualRowResize: true,
      manualRowMove: true,
      dropdownMenu: true,
      filters: true,
      columnSorting: true,
      licenseKey: "non-commercial-and-evaluation",
    };

    const switchEdit = () => {
      isEdit.value = !isEdit.value;

      if (!isEdit.value) {
        selectedUser.value = null;
      }
    };

    const selectedUser = ref(null);
    const editUser = (user) => {
      selectedUser.value = user;
      isEdit.value = !isEdit.value;
    };

    const deleteUser = async (id) => {
      if (!window.confirm(`Хэрэглэгчийн мэдээллийг устгах уу?`)) {
        return;
      }

      try {
        const res = await adminInstance.delete(
          `/yellow-page/delete/meeting-user?id=${id}`
        );
        if (res.status === 200) {
          await fetchUsers();
        }
      } catch (err) {
        return err;
      }
    };

    return {
      isLoading,
      users,
      onPageChanged,
      dataPerPage,
      currentPage,
      totalUsersCount,
      totalPages,
      selectedType,
      hotSettings,
      selectedUser,
      isEdit,
      switchEdit,
    };
  },
};
</script>
